// client/src/components/MessageModal.js
// Reusable modal for sending emails using templates

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import {
  getEmailTemplateByType,
  previewEmailTemplate,
  sendTemplateEmail,
  getPlaceholdersForType
} from '../api/emailTemplates';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';

/**
 * MessageModal - Reusable email composition modal
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {string} templateType - Email template type to use
 * @param {string} recipientEmail - Recipient's email address
 * @param {object} contextData - Data for placeholder replacement
 * @param {number} quoteId - Optional quote ID for context
 * @param {number} opportunityId - Optional opportunity ID for context
 * @param {function} onSuccess - Success callback after email sent
 */
function MessageModal({
  isOpen,
  onClose,
  templateType,
  recipientEmail,
  contextData = {},
  quoteId = null,
  opportunityId = null,
  onSuccess
}) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Email content state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState('');
  const [templateName, setTemplateName] = useState('');

  // Track if user has edited content
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [bodyEdited, setBodyEdited] = useState(false);

  // Available placeholders for this template type
  const [placeholders, setPlaceholders] = useState([]);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  // Load template and preview on open
  useEffect(() => {
    if (isOpen && templateType) {
      loadTemplatePreview();
    }
  }, [isOpen, templateType, contextData]);

  // Set recipient email when provided
  useEffect(() => {
    if (recipientEmail) {
      setRecipient(recipientEmail);
    }
  }, [recipientEmail]);

  const loadTemplatePreview = async () => {
    try {
      setLoading(true);
      setError('');

      // Get template preview with placeholders replaced
      const { preview } = await previewEmailTemplate(templateType, contextData);

      setSubject(preview.subject);
      setBody(preview.body);
      setTemplateName(preview.templateName);

      // Get available placeholders
      const availablePlaceholders = getPlaceholdersForType(templateType);
      setPlaceholders(availablePlaceholders);

      // Reset edited flags
      setSubjectEdited(false);
      setBodyEdited(false);
    } catch (err) {
      console.error('Error loading template:', err);
      setError(err.message || 'Failed to load email template');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      setError('');

      // Validation
      if (!recipient || !recipient.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      if (!subject.trim()) {
        throw new Error('Subject cannot be empty');
      }

      if (!body.trim()) {
        throw new Error('Email body cannot be empty');
      }

      // Send email (manual send, no auto-checks)
      await sendTemplateEmail({
        recipientEmail: recipient,
        templateType,
        contextData,
        quoteId,
        opportunityId,
        isAutomatic: false,
        subjectOverride: subjectEdited ? subject : null,
        bodyOverride: bodyEdited ? body : null
      });

      setSuccess(true);

      // Call success callback after short delay
      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSubject('');
    setBody('');
    setRecipient('');
    setError('');
    setSuccess(false);
    setSubjectEdited(false);
    setBodyEdited(false);
    setShowPlaceholders(false);
    onClose();
  };

  const insertPlaceholder = (placeholderKey) => {
    const placeholder = `{{${placeholderKey}}}`;
    setBody(body + ' ' + placeholder);
    setBodyEdited(true);
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={handleClose} title={`Send Email: ${templateName}`} maxWidth="max-w-3xl">
      {loading ? (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-4">
          {error && <ErrorMessage message={error} />}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
              Email sent successfully!
            </div>
          )}

          {/* Recipient */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To: <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="recipient@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={sending || success}
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject: <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setSubjectEdited(true);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={sending || success}
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Message: <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowPlaceholders(!showPlaceholders)}
                className="text-xs text-primary-600 hover:text-primary-800"
                disabled={sending || success}
              >
                {showPlaceholders ? 'Hide' : 'Show'} Placeholders
              </button>
            </div>

            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setBodyEdited(true);
              }}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              disabled={sending || success}
            />

            {/* Available Placeholders */}
            {showPlaceholders && placeholders.length > 0 && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  Available Placeholders (click to insert):
                </p>
                <div className="space-y-1">
                  {placeholders.map((ph) => (
                    <div key={ph.key} className="flex items-start">
                      <button
                        type="button"
                        onClick={() => insertPlaceholder(ph.key)}
                        className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded font-mono mr-2"
                        disabled={sending || success}
                      >
                        {`{{${ph.key}}}`}
                      </button>
                      <span className="text-xs text-blue-700">{ph.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info Note */}
          {(subjectEdited || bodyEdited) && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-lg text-sm">
              Note: You've edited the template content. These changes will only apply to this email.
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-300"
              disabled={sending || success}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={sending || success || !recipient || !subject.trim() || !body.trim()}
            >
              {sending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : success ? (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Sent!
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default MessageModal;
